// frontend/src/App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Login from './pages/Login';
import MainLandingPage from './pages/MainLandingPage';
import { AuthProvider } from './auth/AuthProvider';
import { NotificationsProvider } from './notifications/NotificationsContext';
import RedirectIfAuthenticated from './auth/RedirectIfAuthenticated';
import RequireAuth from './auth/RequireAuth';
import NavBar from './components/NavBar';

// Heavy routes load on demand: three.js lives only in Market,
// chart.js only in Leaderboard (§5.3).
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const Market = lazy(() => import('./pages/market/Market'));
// Every other page after login loads on demand too, so the landing and login
// pages (the only ones a first visit needs) don't download the game screen,
// framer-motion, the dashboard and the rest up front.
const GameScreen = lazy(() => import('./pages/GameScreen'));
const Home = lazy(() => import('./pages/Home'));
const CampaignDashboard = lazy(() => import('./pages/CampaignDashboard'));
const ItemsStorage = lazy(() => import('./pages/ItemsStorage'));
const AccountScreen = lazy(() => import('./pages/AccountScreen'));
const UpdateLogs = lazy(() => import('./pages/UpdateLogs'));
const Register = lazy(() => import('./pages/Register'));
const Invite = lazy(() => import('./pages/Invite'));

function AppShell() {
  const location = useLocation();

  // 🔒 routes where NavBar should be hidden
  const hideNavOn = ['/', '/login', '/register', '/invite'];
  const hideNav = hideNavOn.includes(location.pathname);
  // The mobile bottom tab bar shows whenever the nav is present. Reserve space
  // for it (the game screen manages its own keyboard clearance in GameScreen.css).
  const hasBottomNav = !hideNav && !location.pathname.startsWith('/game');

  return (
    <>
      {!hideNav && <NavBar />}
      <div className={`app-content${hasBottomNav ? ' with-nav' : ''}`}>
        <Suspense fallback={<div className="route-loading">Saddling the horses…</div>}>
        <Routes>
          {/* Public invite route */}
          <Route path="/invite" element={<Invite />} />

          {/* ✅ Protected Routes */}
          <Route
            path="/home"
            element={
              <RequireAuth>
                <Home />
              </RequireAuth>
            }
          />
          {/* Campaign management is now merged into the Home hub */}
          <Route path="/campaigns" element={<Navigate to="/home" replace />} />
          <Route
            path="/campaign/:campaignId"
            element={
              <RequireAuth>
                <CampaignDashboard />
              </RequireAuth>
            }
          />
          <Route
            path="/campaign/:campaignId/market"
            element={
              <RequireAuth>
                <Market />
              </RequireAuth>
            }
          />
          <Route
            path="/campaign/:campaignId/shop"
            element={
              <RequireAuth>
                <Market />
              </RequireAuth>
            }
          />
          <Route
            path="/campaign/:campaignId/items"
            element={
              <RequireAuth>
                <ItemsStorage />
              </RequireAuth>
            }
          />
          <Route
            path="/game"
            element={
              <RequireAuth>
                <GameScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/leaderboard/:id"
            element={
              <RequireAuth>
                <Leaderboard />
              </RequireAuth>
            }
          />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <AccountScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/updates"
            element={
              <RequireAuth>
                <UpdateLogs />
              </RequireAuth>
            }
          />

          {/* 🔓 Public Routes with redirect if already logged in */}
          <Route
            path="/"
            element={
              <RedirectIfAuthenticated>
                <MainLandingPage />
              </RedirectIfAuthenticated>
            }
          />
          <Route
            path="/login"
            element={
              <RedirectIfAuthenticated>
                <Login />
              </RedirectIfAuthenticated>
            }
          />
          <Route
            path="/register"
            element={
              <RedirectIfAuthenticated>
                <Register />
              </RedirectIfAuthenticated>
            }
          />
        </Routes>
        </Suspense>
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationsProvider>
          <AppShell />
        </NotificationsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
